import type { GameEvent } from "@engine/games/recordEvent.js";
import { SPOTLIE_DECK, type SpotLieRound } from "./decks.js";

export type GameContext = {
  emit: (event: GameEvent) => void;
  done: () => void;
};

export function mountSpotLie(
  root: HTMLElement,
  ctx: GameContext,
  roundIndex: number = 0,
  deck: readonly SpotLieRound[] = SPOTLIE_DECK
): () => void {
  const safeIdx = deck.length > 0 ? ((roundIndex % deck.length) + deck.length) % deck.length : 0;
  const round = deck[safeIdx]!;
  const flagged = new Set<number>();
  let submitted = false;

  function render(): void {
    root.innerHTML = "";

    const intro = document.createElement("p");
    intro.className = "sl-intro";
    intro.textContent = round.intro;
    root.appendChild(intro);

    const para = document.createElement("p");
    para.className = "sl-paragraph";

    round.claims.forEach((claim, i) => {
      if (i > 0) {
        para.appendChild(document.createTextNode(" "));
      }
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "sl-claim";
      btn.setAttribute("aria-pressed", "false");
      btn.textContent = claim.text;
      btn.addEventListener("click", () => onClaimClick(i, btn));
      para.appendChild(btn);
    });

    root.appendChild(para);

    const hint = document.createElement("p");
    hint.className = "sl-hint";
    hint.textContent =
      "Tap each claim you think is fabricated or needs a source. Then press Submit.";
    root.appendChild(hint);

    const submitBtn = document.createElement("button");
    submitBtn.type = "button";
    submitBtn.className = "sl-submit btn-primary";
    submitBtn.textContent = "Submit";
    submitBtn.addEventListener("click", onSubmit);
    root.appendChild(submitBtn);
  }

  function onClaimClick(idx: number, btn: HTMLButtonElement): void {
    if (submitted) return;
    if (flagged.has(idx)) {
      flagged.delete(idx);
      btn.classList.remove("flagged");
      btn.setAttribute("aria-pressed", "false");
    } else {
      flagged.add(idx);
      btn.classList.add("flagged");
      btn.setAttribute("aria-pressed", "true");
    }
  }

  function onSubmit(): void {
    if (submitted) return;
    submitted = true;

    const submitBtn = root.querySelector<HTMLButtonElement>(".sl-submit");
    if (submitBtn) submitBtn.disabled = true;

    const claimBtns = root.querySelectorAll<HTMLButtonElement>(".sl-claim");
    const n = round.claims.length;

    round.claims.forEach((claim, i) => {
      const btn = claimBtns[i];
      const playerFlagged = flagged.has(i);
      const correct = playerFlagged === claim.isLie;

      if (btn) {
        btn.disabled = true;
        btn.classList.remove("flagged");
        if (claim.isLie && playerFlagged) {
          btn.classList.add("sl-correct");
        } else if (claim.isLie && !playerFlagged) {
          btn.classList.add("sl-missed");
        } else if (!claim.isLie && playerFlagged) {
          btn.classList.add("sl-false-flag");
        }
      }

      ctx.emit({
        conceptId: claim.conceptId,
        correct,
        weight: 1 / n,
        gameType: "spotlie",
      });
    });

    const correctCount = round.claims.filter((c, i) => flagged.has(i) === c.isLie).length;

    const msg = document.createElement("p");
    msg.className = "sl-result";
    msg.textContent = `${correctCount} / ${n} correct. Green = lie caught, red = missed or false alarm.`;
    root.appendChild(msg);

    const doneBtn = document.createElement("button");
    doneBtn.type = "button";
    doneBtn.className = "btn-primary";
    doneBtn.textContent = "Continue";
    doneBtn.addEventListener("click", () => {
      doneBtn.disabled = true;
      ctx.done();
    });
    root.appendChild(doneBtn);
  }

  render();

  return () => {
    root.innerHTML = "";
  };
}
